import { Button, ButtonProps } from 'react-aria-components';
import { FieldErrors, Control, UseFormRegister } from 'react-hook-form';

import { DeleteIcon } from 'src/components/children/RecipientCertificateForm';
import DateField from 'src/components/DateField/DateField';
import FileUpload from 'src/components/FileUpload/FileUpload';
import InputText from 'src/components/InputText/InputText';
import SelectField from 'src/components/SelectField/SelectField';

import { getSupportTypes, otherFacilityCities } from '@/logics/children/children';
import { UpdateChildFormData } from '@/pages/mypage';
import { Child } from '@/types/child';

type Props = {
  child: Child | undefined;
  errors: FieldErrors<UpdateChildFormData>;
  control: Control<UpdateChildFormData>;
  register: UseFormRegister<UpdateChildFormData>;
  setSealRegistrationFile: (v: File | undefined) => void;
  recipientCertificateFile1: File | undefined;
  setRecipientCertificateFile1: (v: File | undefined) => void;
  recipientCertificateFile2: File | undefined;
  setRecipientCertificateFile2: (v: File | undefined) => void;
  setRecipientCertificateIsDeleted1: (v: boolean) => void;
  setRecipientCertificateIsDeleted2: (v: boolean) => void;
  recipientCertificateIsLocked1: boolean;
  recipientCertificateIsLocked2: boolean;
};

const RecipientCertificate = ({
  child,
  errors,
  control,
  register,
  setSealRegistrationFile,
  recipientCertificateFile1,
  setRecipientCertificateFile1,
  recipientCertificateFile2,
  setRecipientCertificateFile2,
  setRecipientCertificateIsDeleted1,
  setRecipientCertificateIsDeleted2,
  recipientCertificateIsLocked1,
  recipientCertificateIsLocked2,
}: Props) => {
  type Props = ButtonProps & {
    onPress: () => void;
  };
  const DeleteButton = (props: Props) => {
    return (
      <Button
        className="flex cursor-pointer items-center gap-2 rounded-md px-4 py-2"
        {...props}
      >
        <DeleteIcon />
      </Button>
    );
  };
  return (
    <div className="space-y-4 p-4">
      <div className="space-y-4">
        <div className="text-base font-bold">上限管理</div>
        <div className="text-base">
          {child ? (child.has_upper_management ? 'あり' : 'なし') : '-'}
        </div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="space-y-4">
          <div className="text-base font-bold">他事業所名（1）</div>
          <InputText
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="text"
            {...register('otherFacilityName1')}
          />
          <hr className="border-gray-400" />
        </div>
        <div className="space-y-4">
          <div className="text-base font-bold">他事業所（1）（市区町村）</div>
          <SelectField
            name="otherFacilityCity1"
            control={control}
            items={otherFacilityCities.map((otherFacilityCity) => ({
              label: otherFacilityCity,
              value: otherFacilityCity,
            }))}
          />
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">他事業所名（2）</div>
          <InputText
            className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
            type="text"
            {...register('otherFacilityName2')}
          />
          <hr className="border-gray-400" />
        </div>
        <div className="space-y-4">
          <div className="text-base font-bold">他事業所（2）（市区町村）</div>
          <SelectField
            name="otherFacilityCity2"
            control={control}
            items={otherFacilityCities.map((otherFacilityCity) => ({
              label: otherFacilityCity,
              value: otherFacilityCity,
            }))}
            required={false}
          />
          <hr className="border-gray-400" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">書類確認用印鑑登録</div>
        <FileUpload
          required={false}
          canPreview
          buttonText="画像アップロード"
          accept="image/*"
          setFile={setSealRegistrationFile}
          initialValue={child?.seal_registration_image_url || ''}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">受給者証番号</div>
        <InputText
          className="mb-2 block h-14 w-full rounded-md border border-[#1A1A1A] px-3 py-2 text-base focus:border-2 focus:border-yellow-700 disabled:border-solid-gray-420 disabled:bg-solid-gray-50 disabled:text-solid-gray-420 sm:text-sm"
          type="text"
          required
          errors={errors.recipientCertificateNumber}
          {...register('recipientCertificateNumber')}
        />
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（1）</div>
          <FileUpload
            required
            canPreview
            buttonText="画像アップロード"
            accept="image/*"
            setFile={setRecipientCertificateFile1}
            initialValue={child?.recipient_certificates?.[0].image_url || ''}
            disabled={recipientCertificateIsLocked1}
          />
          <div className="mb-2 flex flex-row gap-2">
            {(recipientCertificateFile1 || child?.recipient_certificates?.[0].image_url) && (
              <DeleteButton onPress={() => setRecipientCertificateIsDeleted1(true)} />
            )}
          </div>
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（1）/適用期間開始</div>
          <DateField
            required
            errors={errors.recipientCertificateStartDate1}
            control={control}
            name="recipientCertificateStartDate2"
          />
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（1）/適用期間終了</div>
          <DateField
            errors={errors.recipientCertificateEndDate1}
            control={control}
            name="recipientCertificateEndDate2"
          />
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（2）</div>
          <FileUpload
            required
            canPreview
            buttonText="画像アップロード"
            accept="image/*"
            setFile={setRecipientCertificateFile2}
            initialValue={
              child?.recipient_certificates && child?.recipient_certificates.length > 1
                ? child?.recipient_certificates?.[1].image_url
                : ''
            }
            disabled={recipientCertificateIsLocked2}
          />
          <div className="mb-2 flex flex-row gap-2">
            {(recipientCertificateFile2 ||
              (child?.recipient_certificates &&
                child?.recipient_certificates.length > 1 &&
                child?.recipient_certificates?.[1].image_url)) && (
              <DeleteButton onPress={() => setRecipientCertificateIsDeleted2(true)} />
            )}
          </div>
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（2）/適用期間開始</div>
          <DateField
            required
            errors={errors.recipientCertificateStartDate2}
            control={control}
            name="recipientCertificateStartDate2"
          />
          <hr className="border-gray-400" />
        </div>

        <div className="space-y-4">
          <div className="text-base font-bold">受給者証（2）/適用期間終了</div>
          <DateField
            required
            errors={errors.recipientCertificateEndDate2}
            control={control}
            name="recipientCertificateEndDate2"
          />
          <hr className="border-gray-400" />
        </div>
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">支援の種類</div>
        <div className="text-base">
          {child?.support_type ? getSupportTypes(child.support_type) : '-'}
        </div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">支給量等</div>
        <div className="text-base">{child?.support_days ? child?.support_days + '日' : '-'}</div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">負担上限月額</div>
        <div className="text-base">
          {child?.max_monthly_burden_amount ? child?.max_monthly_burden_amount + '円' : '-'}
        </div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">特記事項（受給者証）</div>
        <div className="text-base">
          {child?.recipient_certificate_notes ? child.recipient_certificate_notes : '記入なし'}
        </div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">保護者氏名（受給者証に記載のもの）</div>
        <div className="text-base">
          {child?.recipient_certificate_guardian_name
            ? child.recipient_certificate_guardian_name
            : '記入なし'}
        </div>
        <hr className="border-gray-400" />
      </div>

      <div className="space-y-4">
        <div className="text-base font-bold">保護者生年月日（受給者証に記載のもの）</div>
        <div className="text-base">{child?.recipient_certificate_guardian_birth_date}</div>
        <hr className="border-gray-400" />
      </div>
    </div>
  );
};

export default RecipientCertificate;
